<template>
  <div
    ref="editor"
    v-clickoutside="handleBlur"
    :class="[
      {'editor--disabled': disabled}, 
      {'editor--fixed-height': fixedHeight },
      {'editor--disable-toolbar-stickyness': disableToolbarStickyness },
    ]"
    class="editor"
    @click="handleFocus"
  >
    <!-- Variables -->
    <dropdown 
      v-if="hasVariables" 
      :force-visibility="showVariableDropdown" 
      class="insert-variable-dropdown"
      placement="bottom-end"
    >
      <el-button class="insert-variable-button">
        Insert Variable
        <i class="ddi ddi-plus-thick" />
      </el-button>
      <template #content>
        <div>
          <ul>
            <li
              v-for="(item, index) in variables"
              :key="index"
              :class="getCopyableVariableClassIfNecessary(item)"
              :data-clipboard-text="item.name"
              @click="handleVariable(item)"
            >{{ item.description }}</li>
          </ul>
        </div>
      </template>
    </dropdown>
    
    <div :id="`toolbar-${name}`" ref="toolbar">
      <!-- Toolbar buttons -->
      <!-- To disable tools on mobile, do it via CSS: Search for "Hide certain tools on mobile." comment -->
      <button class="ql-bold" />
      <button class="ql-italic" />
      <button class="ql-underline" />
      <button class="ql-header" value="2" />
      <button class="ql-link" />
      <button class="ql-image" />
      <button class="ql-blockquote" />
      <button class="ql-code-block" />
      <button class="ql-list" value="bullet" />
      <button class="ql-list" value="ordered" />
      <button class="ql-table-better" />

      <!-- Attachment button -->
      <button
        v-if="allowsAttachment"
        :disabled="uploadingFile"
        type="button"
        @click="triggerUpload"
      >
        <i class="ddi ddi-attachment toolbar-icon" />
      </button>

      <!-- DESKTOP only: Google Drive -->
      <google-picker v-if="allowsGoogleDrive && !$screen.isMobile($mq)" @picked="handleGoogleDriveCallback" />
     
      <!-- Saved replies -->
      <editor-saved-replies
        v-if="hasSavedReplies"
        :is-disabled="disabled"
        :message="modelValue"
        :saved-replies="savedReplies"
        @selected-saved-reply="overwriteBody"
      />

      <!-- Saved templates -->
      <editor-saved-templates
        v-if="hasSavedTemplates"
        :is-disabled="disabled"
        :message="modelValue"
        :saved-templates="savedTemplates"
        @selected-saved-template="overwriteBody"
      />
    </div>

    <!-- Wrapper with editor and attachment wrapper -->
    <div
      :class="{
        'flat-bottom': flatBottom,
        'focus': isEditorFocused,
        'upload': (hasAttachment || existingAttachments.length > 0)
      }"
      class="editor__canvas"
    >
      <div ref="editorCanvas" class="editor__canvas--content" />
      <div
        v-if="allowsAttachment"
        v-show="dragEnter"
        :class="{'dropzone__drag-over': dragOver}"
        class="dropzone"
      >
        <div id="textnode" class="dropzone__label">
          <div>
            <i class="ddi ddi-file" />
            <i class="ddi ddi-image" />
          </div>
          <span>Drop files or images here</span>
        </div>
      </div>
      <div v-if="signature" class="content-signature">
        <p class="signature__text">--</p>
        <div class="signature__text" v-html="signature" />
      </div>
    </div>
    <!-- File Upload -->
    <el-upload
      v-show="hasAttachment || existingAttachments.length > 0"
      ref="upload"
      :on-change="handleChange"
      :file-list="fileList"
      :on-remove="handleRemove"
      :on-error="handleError"
      :on-progress="handleChange"
      :auto-upload="false"
      :disabled="uploadingFile"
      :class="{ uploading: uploadingFile, 'flat-bottom': flatBottom }"
      action=" "
      drag
      class="editor__upload-list"
      multiple
    >
      <button ref="btnUpload" type="button" class="dd-attachment__button" @click="handlerPicker" />
      <template #tip>
        <div class="el-upload__tip" />
      </template>
    </el-upload>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent, markRaw } from 'vue'
import Quill from 'quill'
import { Mention, MentionBlot } from 'quill-mention'
import QuillTableBetter from 'quill-table-better'
import * as linkify from 'linkifyjs'
import { ClickOutside as Clickoutside } from 'element-plus'

/* Mixins */

/* Components */
import GooglePicker from '@/components/editor/components/editor-google-picker.vue'
import EditorSavedReplies from '@/components/editor/components/editor-saved-replies.vue'
import EditorSavedTemplates from '@/components/editor/components/editor-saved-templates.vue'

/* Helpers */
import ClipboardJS from 'clipboard'
import FileUploadAssistant from '@/helpers/file-upload-assistant'

/* Data types */
import { IBrowser } from './editor.types'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IConvertedSavedReply,
  IDescriptionTemplate,
  INameAndDescription,
} from '@/api/types/response.types'

/* API */
import FilesAPI from '@/api/modules/files.api'
import TicketPortalAPI from '@/api/modules/ticket-portal.api'

Quill.register({ 'blots/mention': MentionBlot, 'modules/mention': Mention })
// Registering the table module also replaces Quill's toolbar and clipboard modules with its own, which handle table cells
Quill.register({ 'modules/table-better': QuillTableBetter }, true)

const Delta = Quill.import('delta')

// Images go into the text (as data: URIs, which the API uploads); other dropped files are attached.
const IMAGE_MIME_REGEX = /^image\/(p?jpg|jpeg|gif|png)$/i

// The attributes a saved table keeps on each of its elements (see getHTML)
const TABLE_ATTRIBUTES: { [element: string]: string[] } = {
  table: [],
  colgroup: [],
  col: ['width'],
  thead: [],
  tbody: [],
  tr: [],
  th: ['colspan', 'rowspan', 'width'],
  td: ['colspan', 'rowspan', 'width'],
}

// quill-table-better's classes for the lines in a table cell
const TABLE_LINE_CLASSES = [
  'ql-table-block',
  'table-th-block',
  'ql-table-header',
  'table-list',
]

// Marks a piece of a header cell's text as ending in a line end for the table module's clipboard matcher (see mounted).
// A private-use character, which text doesn't have.
const TABLE_HEADER_MARK = '\uE000\n'

/**
 * WYSIWYG editor using QuillJS
 */
export default defineComponent({
  name: 'Editor',

  directives: { Clickoutside },

  components: {
    GooglePicker,
    EditorSavedReplies,
    EditorSavedTemplates,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },

    name: {
      type: String,
      default: 'editor',
    },

    fixedHeight: {
      type: Boolean,
      default: false,
    },

    /**
     * Reply signature
     */
    signature: {
      type: String,
      default: '',
    },

    /**
     * Array of elements to be mentioned by at
     */
    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      default: (): IAtMentionUser[] => [],
    },

    /**
     * Array of strings with custom variables
     */
    variables: {
      type: Array as () => INameAndDescription[],
      required: false,
      default: (): INameAndDescription[] => [],
    },

    placeholder: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    allowsAttachment: {
      type: Boolean,
      default: false,
    },

    allowsGoogleDrive: {
      type: Boolean,
      default: false,
    },

    // When coupled with allowsAttachment, will send file upload requests
    // to ticket portal customer API instead of normal account membership files API...
    ticketPortalConvoId: {
      type: Number,
      default: null,
    },

    // Pre-stub attachments list if necessary (e.g. this is used for editing an existing comment)
    existingAttachments: {
      type: Array as () => IAttachment[],
      default: (): IAttachment[] => [],
    },

    autofocus: {
      type: Boolean,
      default: false,
    },

    savedReplies: {
      type: Array as () => IConvertedSavedReply[],
      default: (): IConvertedSavedReply[] => [],
    },

    savedTemplates: {
      type: Array as () => IDescriptionTemplate[],
      default: (): IDescriptionTemplate[] => [],
    },

    clearAttachments: {
      type: Boolean,
      default: false,
    },

    flatBottom: {
      type: Boolean,
      default: false,
    },

    disableToolbarStickyness: {
      type: Boolean,
      default: false,
    },

    /* Overrides any top pixel value for the sticky toolbar with the inputed value.*/
    customStickyTopPixels: {
      type: Number,
      default: null,
    },
  },

  emits: [
    'cleared-attachments',
    'file-list-removed',
    'files-list',
    'update:modelValue',
    'mentions-list',
    'open-editor',
  ],

  data() {
    return {
      editor: null as any,
      clipboard: null as any,
      // The listeners the editor's modules added on document (see mounted)
      documentListeners: [] as any[][],
      copyableVariableClass: 'copyable-var',
      showVariableDropdown: false,
      output: '',
      matches: [] as any[],
      fileList: [] as any[],
      filelistRemoved: [] as any[],
      isEditorFocused: false,
      hasAttachment: false,
      uploadingFile: false,
      browser: this.$deviceInfo.getBrowser as IBrowser,
      isMobileActionSheetOpen: false,
      dragEnter: false,
      dragOver: false,
      variableValueAndLabelDelimiter: '###',
    }
  },

  computed: {
    hasVariables(): boolean {
      return this.variables.length > 0
    },
    hasLinkInVariables(): boolean {
      if (!this.hasVariables) {
        return false
      }
      return this.variables.some(v => this.isVariableAUrl(v))
    },
    hasSavedReplies(): boolean {
      return this.savedReplies.length > 0
    },
    hasSavedTemplates(): boolean {
      return this.savedTemplates.length > 0
    },
    quillMentionUsers(): any {
      /* Quill requires id, value fields so remap the mentionUsers here */
      return this.mentionUsers.map((item: any) => ({
        id: item.id,
        value: item.label,
        label: item.name,
      }))
    },
    isInternetExplorer(): boolean {
      return this.browser.name === 'Internet Explorer'
    },
    isFirefox(): boolean {
      return this.browser.name === 'Firefox'
    },
  },

  watch: {
    modelValue(value, oldValue) {
      if (value !== this.output && !this.isInternetExplorer) {
        this.setHTML(value)
      }
      if (value === '') {
        this.fileList = []
        this.hasAttachment = false
      }
    },
    existingAttachments: {
      deep: 1,
      handler(value) {
        if (value && value.length === 0) {
          this.hasAttachment = false
        }
        this.fileList = value
      },
    },

    /**
     * Clear file list when true
     */
    clearAttachments(value: boolean) {
      if (value) {
        this.fileList = []
        this.hasAttachment = false
        this.$emit('cleared-attachments')
      }
    },

    autofocus: {
      immediate: true,
      async handler(value) {
        this.isEditorFocused = value
        if (value) {
          await this.$nextTick()
          // @ts-ignore
          this.editor.focus()
        }
      },
    },
  },

  mounted() {
    if (this.hasLinkInVariables) {
      this.clipboard = markRaw(new ClipboardJS('.' + this.copyableVariableClass))
      this.clipboard.on('success', (e: any) => {
        // @ts-ignore
        this.$messaging.showSuccess('Copied to your clipboard!')
        e.clearSelection()
      })
    }
    if (this.existingAttachments.length) {
      this.fileList = this.existingAttachments
    }

    this.hasAttachment = this.fileList.length > 0

    /**
     * Shared quill modules
     */
    const modules = {
      toolbar: `#toolbar-${this.name}`,
      /**
       * Pasted image files (dropped files go through handleDrop)
       */
      uploader: {
        mimetypes: ['image/png', 'image/jpeg', 'image/gif'],
      },
      /**
       * Module Mention
       */
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        defaultMenuOrientation: 'bottom',
        // DOM nodes, not HTML: quill-mention shows a string as text
        renderItem: (item: any, searchTerm: string) => {
          const name = document.createElement('span')
          name.className = 'mention--dark'
          name.textContent = item.label
          const handle = document.createElement('span')
          handle.className = 'mention--grey'
          handle.textContent = `@${item.value}`
          const rendered = document.createElement('span')
          rendered.appendChild(name)
          rendered.appendChild(document.createTextNode(' '))
          rendered.appendChild(handle)
          return rendered
        },
        source: (searchTerm: string, renderList: any, mentionChar: any) =>
          this.searchMentions(searchTerm, renderList, mentionChar),
      },
      /**
       * Tables. Without the table and cell properties menus: they set colors, borders and alignment, which aren't saved
       */
      'table-better': {
        language: 'en_US',
        menus: ['column', 'row', 'merge', 'wrap', 'delete'],
        toolbarTable: true,
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }

    /**
     * Custom quill icons
     */
    const icons = Quill.import('ui/icons') as Record<string, any>
    icons.bold = '<i class="ddi ddi-bold toolbar-icon"/>'
    icons.header['2'] = '<i class="ddi ddi-heading toolbar-icon"/>'
    icons.italic = '<i class="ddi ddi-italic toolbar-icon"/>'
    icons.image = '<i class="ddi ddi-image toolbar-icon"/>'
    icons.underline = '<i class="ddi ddi-underline toolbar-icon"/>'
    icons.link = '<i class="ddi ddi-link toolbar-icon"/>'
    icons.blockquote = '<i class="ddi ddi-quotes toolbar-icon"/>'
    icons['code-block'] = '<i class="ddi ddi-code toolbar-icon"/>'
    icons.list.bullet = '<i class="ddi ddi-bulleted-list toolbar-icon"/>'
    icons.list.ordered = '<i class="ddi ddi-numbered-list toolbar-icon"/>'
    // The icon font has no table
    icons['table-better'] =
      '<svg class="toolbar-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">' +
      '<rect x="1" y="2" width="14" height="12" rx="1.5"/><path d="M1 6h14M1 10h14M8 6v8"/></svg>'

    /**
     * Shared quill configurations
     */
    const configQuill = {
      placeholder: this.placeholder,
      theme: 'snow',
      bounds: this.$refs.editorCanvas as HTMLElement, // Ensures tooltip stays inside the ql-editor boundary (and doesn't veer under toolbar, for instance)
      formats: [
        'bold',
        'italic',
        'link',
        'strike',
        'underline',
        'script',
        'blockquote',
        'header',
        'indent',
        'list',
        'code-block',
        'direction',
        'image',
        'video',
        'mention',
        // quill-table-better's blots
        'table-container',
        'table-temporary',
        'table-colgroup',
        'table-col',
        'table-thead',
        'table-th-row',
        'table-th',
        'table-th-block',
        'table-body',
        'table-row',
        'table-cell',
        'table-cell-block',
        'table-header',
        'table-list-container',
        'table-list',
      ], // Whitelist formatting options (removes inline styling from being applied on copy-paste)
    }

    // The table module listens on document (for copy, cut, paste and keyup, and for clicks outside its table picker) and
    // never stops, which would keep this editor once it's gone, so what Quill's modules add there is taken off with it
    // (see beforeUnmount)
    const listen = document.addEventListener
    const ownListen = Object.prototype.hasOwnProperty.call(
      document,
      'addEventListener',
    )
    document.addEventListener = (...listener: any[]) => {
      this.documentListeners.push(listener)
      listen.apply(document, listener as Parameters<typeof listen>)
    }

    try {
      // Raw, so Vue doesn't proxy Quill, which compares its blots by identity
      this.editor = markRaw(
        new Quill(this.$refs.editorCanvas as HTMLElement, {
          modules,
          ...configQuill,
        }),
      )
    } finally {
      // As it was: usually the method from document's prototype, which a script may wrap later
      if (ownListen) {
        document.addEventListener = listen
      } else {
        delete document.addEventListener
      }
    }

    this.$refs.editorCanvas
      // @ts-ignore
      .querySelector('.ql-editor')
      .classList.add('user-inputed-rich-text')

    if (this.customStickyTopPixels !== null) {
      this.$refs.editor
        // @ts-ignore
        .querySelector('.ql-toolbar').style.cssText = `top: ${
        this.customStickyTopPixels
      }px;`
    }

    // Dropped files are handled here, before Quill's uploader would insert any dropped image
    this.editor.container.addEventListener('drop', this.handleDrop, true)

    // While table cells are selected, the table module acts on copy, paste and Backspace anywhere on the page (through
    // listeners of its own on document), so let go of them when the user turns to something else
    document.addEventListener('mousedown', this.releaseTableCells, true)
    document.addEventListener('focusin', this.releaseTableCells, true)

    // Quill ends a td's text with a line end but not a th's, so the table module's clipboard matcher ends every piece of
    // a header cell's text with one, which turned <th><p>a <strong>b</strong></p></th> into three lines (in a saved
    // table, each time it was loaded). The matcher gets the pieces before the last marked as already ending in one, and
    // the marks come out after.
    const cellMatcher = this.editor.clipboard.matchers.find(
      (matcher: any) => matcher[0] === 'td, th',
    )
    const matchCell = cellMatcher[1]
    cellMatcher[1] = (node: HTMLElement, delta: any, scroll: any) => {
      if (node.tagName !== 'TH') {
        return matchCell(node, delta, scroll)
      }

      delta.ops.slice(0, -1).forEach((op: any) => {
        if (typeof op.insert === 'string' && !op.insert.endsWith('\n')) {
          op.insert += TABLE_HEADER_MARK
        }
      })

      const matched = matchCell(node, delta, scroll)
      matched.ops = matched.ops
        .map(
          (op: any) =>
            typeof op.insert === 'string'
              ? { ...op, insert: op.insert.split(TABLE_HEADER_MARK).join('') }
              : op,
        )
        .filter((op: any) => op.insert !== '')
      return matched
    }

    // Change the link placeholder
    const tooltip = this.editor.theme.tooltip
    const input = tooltip.root.querySelector('input[data-link]')
    input.dataset.link = 'Enter link address here...'

    this.setHTML(this.modelValue, 'silent')

    this.editor.enable(!this.disabled)

    /**
     * Autolink pasted URL's
     */
    this.editor.clipboard.addMatcher(
      Node.TEXT_NODE,
      (node: any, delta: any) => {
        if (typeof node.data !== 'string') {
          return
        }
        // Not a link's text, which keeps the link's own URL. (Saved replies and templates come through here too now,
        // as setHTML reads HTML through the clipboard.)
        if (node.parentElement && node.parentElement.closest('a[href]')) {
          return delta
        }
        // @ts-ignore
        const links = linkify.find(node.data)

        if (links && links.length > 0) {
          const ops: any = []
          let str = node.data
          links.forEach((match: any) => {
            const split = str.split(match.value)
            const beforeLink = split.shift()
            ops.push({ insert: beforeLink, attributes: {} })
            ops.push({ insert: match.value, attributes: { link: match.href } })
            str = split.join(match)
            str = split.join(' ')
          })
          ops.push({ insert: str })
          delta.ops = ops
        }

        if (!this.isFirefox) {
          const filtered = delta.ops.filter((item: any) => {
            return !IMAGE_MIME_REGEX.test(item.insert)
          })
          delta.ops = filtered
        }
        return delta
      },
    )

    /**
     * A table pasted after text on a line goes on a line of its own, as an inserted table does: the table's first line
     * holds its attributes and doesn't show, and the table module's clipboard would leave that text on it
     */
    const clipboard = this.editor.clipboard
    const paste = clipboard.onPaste.bind(clipboard)
    clipboard.onPaste = (
      range: any,
      data: { html?: string; text?: string },
    ) => {
      const [, offset] = this.editor.getLine(range.index)
      const formats = this.editor.getFormat(range.index)
      const first = clipboard.convert(data, formats).ops[0]

      if (
        offset > 0 &&
        !formats['table-cell-block'] &&
        !formats['table-th-block'] &&
        first &&
        first.attributes &&
        first.attributes['table-temporary']
      ) {
        this.editor.insertText(range.index, '\n', 'user')
        range = { index: range.index + 1, length: range.length }
      }

      paste(range, data)
    }

    /**
     * Autolink URLs when typing
     */
    this.editor.on(
      'text-change',
      async (delta: any, oldDelta: any, source: any) => {
        const hitBreak =
          delta.ops.length === 2 && this.isWhitespace(delta.ops[1].insert)
        if (hitBreak) {
          const content = this.editor.getContents().ops
          const newOps: any[] = this.updateDelta(content)
          if (content.length !== newOps.length) {
            /**
             * This updates the content of quill editor and
             * set selection to set the cursor at the end of the
             * text inserted
             */
            this.$nextTick(() => {
              // As a change, not new contents: setContents drops the table module's cells
              this.editor.updateContents(
                new Delta(content).diff(new Delta(newOps)),
              )
              this.editor.setSelection(delta.length())
            })
          }
        }
        this.update()
      },
    )

    /**
     * To allow scroll on quill-mention
     */
    window.addEventListener('keydown', ev => {
      const mention: HTMLElement = document.querySelector('.ql-mention-list')
      const mentionItem: HTMLElement = document.querySelector('.selected')
      if (mention) {
        if (ev.keyCode === 38 || ev.keyCode === 40) {
          if (mentionItem) {
            mention.scrollTop = mentionItem.offsetTop - 6
          }
        }
      }
    })

    /**
     * Focus and set selection on IE when clicking on editor
     * without this the click only works on the first line
     */
    if (this.isInternetExplorer) {
      document.querySelector('.editor__canvas').addEventListener('click', e => {
        this.$nextTick(() => {
          const selection = this.editor.getSelection()
          const text = this.editor.getText().length
          selection
            ? this.editor.setSelection(selection)
            : this.editor.setSelection(text)
        })
      })
    }

    if (this.allowsAttachment && !this.isInternetExplorer) {
      this.addEventListenerDrag()
    }

    // As Quill 1 did once it had read the value: give the parent the HTML as it will be saved (an empty value it left
    // as it was)
    if (this.editor.getLength() > 1) {
      this.update()
    }
  },

  beforeUnmount() {
    if (this.clipboard) {
      this.clipboard.destroy()
    }
    this.editor.container.removeEventListener('drop', this.handleDrop, true)
    document.removeEventListener('mousedown', this.releaseTableCells, true)
    document.removeEventListener('focusin', this.releaseTableCells, true)
    this.documentListeners.forEach((listener: any[]) =>
      document.removeEventListener.apply(
        document,
        listener as Parameters<typeof document.removeEventListener>,
      ),
    )
    document.removeEventListener('dragenter', this.activateDragEnter)
    document.removeEventListener('dragover', this.activateDragEnter)
    document.removeEventListener('mouseout', this.handleMouseOut)
    document.removeEventListener('dragend', this.cancelDragEnter)
    document
      .querySelector('body')
      .removeEventListener('mouseout', this.cancelDragEnter)

    this.editor.container.removeEventListener('dragover', this.activateDragOver)
    this.editor.container.removeEventListener('dragleave', this.cancelDragOver)
  },

  methods: {
    /*
    * Add event listener drag
    */
    addEventListenerDrag() {
      /**
       *  Add event to catch drag over window and activate "drop here" message
       */
      document.addEventListener('dragenter', this.activateDragEnter)
      document.addEventListener('dragover', this.activateDragEnter)
      this.editor.container.addEventListener('dragover', this.activateDragOver)

      /** Add event to catch drag end to remove "drop here" message and catch drag leave editor and activate animation */
      document.addEventListener('dragend', this.cancelDragEnter)
      this.editor.container.addEventListener('dragleave', this.cancelDragOver)

      /** Add event to catch mouse out of body to remove "drop here" message */
      document
        .querySelector('body')
        .addEventListener('mouseout', this.cancelDragEnter)

      /** Add event to catch mouse leaving window to remove "drop here" message */
      document.addEventListener('mouseout', this.handleMouseOut)
    },

    /**
     * When mouse goes outside the window remove "drop here" message
     */
    handleMouseOut(event: MouseEvent) {
      if (
        // @ts-ignore
        event.toElement == null &&
        this.dragEnter === true &&
        event.relatedTarget == null
      ) {
        this.dragEnter = false
      }
    },

    /**
     * Activates drag over editor
     */
    activateDragOver(event: DragEvent) {
      this.dragOver = true
    },

    /**
     * Cancels drag over editor
     */
    cancelDragOver(event: DragEvent) {
      event.preventDefault()
      event.stopPropagation()
      this.dragOver = false
    },

    /**
     * Activates drag on window and to diplay message
     */
    activateDragEnter(event: DragEvent) {
      event.preventDefault()
      event.stopPropagation()
      this.dragEnter = true
    },

    /**
     * Deactivate drag on window and to display message
     */
    cancelDragEnter(event: DragEvent | MouseEvent) {
      event.preventDefault()
      event.stopPropagation()
      this.dragEnter = false
    },

    /**
     * Update delta to add links on typing
     */
    updateDelta(content: any) {
      const newOps: any[] = []
      content.forEach((el: any) => {
        if (typeof el.insert === 'string') {
          // @ts-ignore
          const url = linkify.find(el.insert)
          if (url.length > 0) {
            const before = el.insert.split(url[0].value)[0]
            const after = el.insert.split(url[0].value)[1]

            if (before) {
              newOps.push({ ...el, insert: before })
            }

            newOps.push({
              // @ts-ignore
              insert: url[0].value,
              attributes: { link: url[0].href },
            })

            if (after) {
              newOps.push({ ...el, insert: after })
            }
          } else {
            newOps.push({ ...el })
          }
        } else {
          newOps.push({
            ...el,
            insert: el.insert,
          })
        }
      })

      return newOps
    },

    isWhitespace(text: string) {
      return text === ' ' || text === '\t' || text === '\n'
    },

    isVariableAUrl(nad: INameAndDescription) {
      return nad.name.toLowerCase().startsWith('http')
    },

    getCopyableVariableClassIfNecessary(nad: INameAndDescription) {
      return this.isVariableAUrl(nad) ? this.copyableVariableClass : ''
    },

    update() {
      this.output = this.getHTML()
      this.$emit('update:modelValue', this.output || '')
      this.emitSelectedAtMentionIDs()
    },

    /**
     * The editor's content as HTML, written as Quill 1 wrote it, which is what the API, the display styles
     * (nestedLists) and the notification emails expect: Quill 2's own HTML has every list as an <ol> and code blocks
     * as divs. (Its getSemanticHTML nests lists instead, writes every space as &nbsp; and drops blank lines' <br>.)
     * Tables, which Quill 1 didn't have, are plain HTML tables.
     * An empty editor is '<p><br></p>', which forms compare against to tell it's empty.
     */
    getHTML(): string {
      if (this.editor.getLength() <= 1) {
        return '<p><br></p>'
      }

      const root = this.editor.root.cloneNode(true) as HTMLElement

      // A list's items, split into a <ul> or <ol> per run of one kind (Quill 2 keeps them all in one <ol>, by data-list)
      Array.from(root.querySelectorAll('ol')).forEach((container: Element) => {
        let list: Element = null
        let kind: string = null

        Array.from(container.children).forEach((item: Element) => {
          const item_kind = item.getAttribute('data-list') || 'ordered'

          if (item_kind !== kind) {
            kind = item_kind
            list = document.createElement(kind === 'ordered' ? 'ol' : 'ul')

            if (kind === 'checked' || kind === 'unchecked') {
              list.setAttribute(
                'data-checked',
                kind === 'checked' ? 'true' : 'false',
              )
            }

            container.parentNode.insertBefore(list, container)
          }

          item.removeAttribute('data-list')
          list.appendChild(item)
        })

        container.parentNode.removeChild(container)
      })

      // A code block, one div per line, as a <pre>
      Array.from(root.querySelectorAll('.ql-code-block-container')).forEach(
        (container: Element) => {
          const pre = document.createElement('pre')
          pre.className = 'ql-syntax'
          pre.setAttribute('spellcheck', 'false')
          pre.textContent = Array.from(
            container.querySelectorAll('.ql-code-block'),
          )
            .map(line => `${line.textContent}\n`)
            .join('')
          container.parentNode.replaceChild(pre, container)
        },
      )

      // Quill 2's controls, such as a list item's marker
      Array.from(root.querySelectorAll('.ql-ui')).forEach((ui: Element) =>
        ui.parentNode.removeChild(ui),
      )

      // A table as plain HTML, which the table module's clipboard matchers read back: without the line holding the
      // table's own attributes, cell ids, the selection and styling (which isn't saved), but with the widths that
      // resizing sets
      Array.from(root.querySelectorAll('table')).forEach(
        (table: HTMLTableElement) => {
          const width = table.style.width

          Array.from(table.querySelectorAll('temporary')).forEach(
            (line: Element) => line.parentNode.removeChild(line),
          )

          Array.from(table.querySelectorAll('*'))
            .concat(table)
            .forEach((element: Element) => {
              const kept = TABLE_ATTRIBUTES[element.tagName.toLowerCase()]

              Array.from(element.attributes).forEach((attribute: Attr) => {
                if (
                  kept
                    ? kept.indexOf(attribute.name) < 0
                    : attribute.name === 'data-cell'
                ) {
                  element.removeAttribute(attribute.name)
                }
              })

              if (!kept && element.classList.length > 0) {
                TABLE_LINE_CLASSES.forEach(name =>
                  element.classList.remove(name),
                )

                if (element.classList.length === 0) {
                  element.removeAttribute('class')
                }
              }
            })

          if (/^\d+(\.\d+)?(px|%)$/.test(width)) {
            table.setAttribute('style', `width: ${width}`)
          }
        },
      )

      // Spaces that HTML collapses, though the editor shows them (so they'd also be lost when the text is next loaded): a
      // run of them, also one that formatting splits (as in "<strong>a </strong> b"), and one at a line's start or end.
      // They alternate with non-breaking ones, as browsers write them, a run ending in a normal space where the line can
      // wrap
      const nbsp = String.fromCharCode(0xa0)
      const lineSelector = 'p, li, h1, h2, h3, h4, h5, h6, blockquote'

      // A line's text, and its embeds (such as a mention, whose own text is left as it is)
      const lineParts = (node: Node): Node[] =>
        node.nodeType === Node.TEXT_NODE ||
        (node instanceof Element &&
          node.matches('img, [contenteditable="false"]'))
          ? [node]
          : Array.from(node.childNodes).reduce(
              (parts: Node[], child: Node) => parts.concat(lineParts(child)),
              [],
            )

      Array.from(root.querySelectorAll(lineSelector))
        .filter(
          (line: Element) =>
            / {2}|^ | $/.test(line.textContent) &&
            !line.querySelector(lineSelector),
        )
        .forEach((line: Element) => {
          const parts = lineParts(line).map((node: Node) => ({
            node,
            text:
              node.nodeType === Node.TEXT_NODE
                ? node.nodeValue.split('')
                : null,
          }))
          let run: Array<{ text: string[]; index: number }> = []
          let lineStart = true

          // The spaces just read, which something else or the line's end follows
          const endRun = (lineEnd: boolean) => {
            run.forEach(({ text, index }, i) => {
              if (
                (run.length - 1 - i) % 2 === 1 ||
                (lineStart && i === 0) ||
                (lineEnd && i === run.length - 1)
              ) {
                text[index] = nbsp
              }
            })

            run = []
          }

          // Anything else: a character or an embed
          const readContent = () => {
            endRun(false)
            lineStart = false
          }

          parts.forEach(({ text }) => {
            if (!text) {
              readContent()
              return
            }

            text.forEach((char: string, index: number) => {
              if (char === ' ') {
                run.push({ text, index })
              } else {
                readContent()
              }
            })
          })

          endRun(true)

          parts.forEach(({ node, text }) => {
            if (text) {
              node.nodeValue = text.join('')
            }
          })
        })

      return root.innerHTML
    },

    /**
     * Replaces the editor's content. Through the clipboard, which also reads HTML that Quill 1 wrote (e.g. its lists),
     * and as an update, since setContents' quicker path drops the table module's cells.
     */
    setHTML(html: string, source: string = 'api') {
      // convertHTML, as convert drops a last empty line (such as the one after a table)
      const contents = html
        ? this.editor.clipboard.convertHTML(html)
        : new Delta()
      const last = contents.ops[contents.ops.length - 1]

      // Ending on a line of its own, not on the replaced content's last line, whose formats it would take
      if (
        !last ||
        typeof last.insert !== 'string' ||
        !last.insert.endsWith('\n')
      ) {
        contents.insert('\n')
      }

      this.editor.updateContents(
        contents.delete(this.editor.getLength()),
        source,
      )
    },

    /**
     * Unselects any selected table cells when the user turns to something outside the editor (see mounted).
     */
    releaseTableCells(event: Event) {
      const table = this.editor.getModule('table-better')

      if (
        table.cellSelection.selectedTds.length > 0 &&
        !(this.$refs.editor as HTMLElement).contains(event.target as Node)
      ) {
        table.hideTools()
      }
    },

    /**
     * Dropped files: images go into the text where they're dropped, anything else is attached. Only where attachments
     * are allowed; files dropped elsewhere are ignored.
     */
    handleDrop(event: DragEvent) {
      const files: File[] = event.dataTransfer
        ? Array.from(event.dataTransfer.files)
        : []

      if (files.length === 0) {
        // E.g. text dragged within the editor, which Quill moves itself
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (!this.allowsAttachment) {
        return
      }

      this.dragEnter = false
      this.dragOver = false
      this.$emit('open-editor')

      files
        .filter(file => !IMAGE_MIME_REGEX.test(file.type))
        .forEach((file, index) => {
          this.handleChange(
            {
              status: 'ready',
              name: file.name,
              size: file.size,
              percentage: 0,
              raw: file,
              uid: Date.now() + index,
            },
            this.fileList,
          )
        })

      this.insertImages(
        this.getDropRange(event),
        files.filter(file => IMAGE_MIME_REGEX.test(file.type)),
      )
    },

    /**
     * Where in the text a drop landed, found as Quill's own uploader finds it; else the current selection.
     */
    getDropRange(event: DragEvent): any {
      let native: Range = null

      if (document.caretRangeFromPoint) {
        native = document.caretRangeFromPoint(event.clientX, event.clientY)
      } else if ((document as any).caretPositionFromPoint) {
        const position = (document as any).caretPositionFromPoint(
          event.clientX,
          event.clientY,
        )
        native = document.createRange()
        native.setStart(position.offsetNode, position.offset)
        native.setEnd(position.offsetNode, position.offset)
      }

      const normalized = native && this.editor.selection.normalizeNative(native)

      return normalized
        ? this.editor.selection.normalizedToRange(normalized)
        : this.editor.getSelection(true)
    },

    /**
     * Inserts images at a range as data: URIs, as Quill's uploader does.
     */
    insertImages(range: any, files: File[]) {
      if (files.length === 0) {
        return
      }

      Promise.all(
        files.map(
          file =>
            new Promise<string>(resolve => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result as string)
              reader.readAsDataURL(file)
            }),
        ),
      ).then(images => {
        const update = images.reduce(
          (delta: any, image: string) => delta.insert({ image }),
          new Delta().retain(range.index).delete(range.length),
        )
        this.editor.updateContents(update, 'user')
        this.editor.setSelection(range.index + images.length, 'silent')
      })
    },

    async handleVariable(nad: INameAndDescription) {
      // e.g. {ReplierName} or https://www....
      const value = nad.name
      // e.g. 'Replier Full Name' or 'Copy link to Ticket Portal'
      const label = nad.description
      const isUrl = this.isVariableAUrl(nad)
      const cursorPosition = this.getCurrentCursorPosition()
      let newCursorPosition

      // Note: URLs are attached to clipboardJS for copying (currently just for ticket portal URL)
      if (!isUrl) {
        this.editor.insertText(cursorPosition, value)
        newCursorPosition = cursorPosition.index + value.length
      }
      this.editor.setSelection(newCursorPosition)

      this.showVariableDropdown = true
      this.$nextTick(() => {
        this.showVariableDropdown = false
      })
    },

    /*
     * If none of the values exist (getSelection and savedRange)
     * return the length of the text
     */
    getCurrentCursorPosition(): any {
      if (this.editor.getSelection() && this.editor.getSelection().index) {
        return this.editor.getSelection().index
      }
      if (this.editor.selection && this.editor.selection.savedRange) {
        return this.editor.selection.savedRange
      }
      return this.editor.getLength() - 1
    },

    searchMentions(searchTerm: string, renderList: any, mentionChar: string) {
      if (searchTerm.length === 0) {
        renderList(this.quillMentionUsers, searchTerm)
      } else {
        this.matches = []
        for (const i in this.quillMentionUsers) {
          if (
            ~this.quillMentionUsers[i].value
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase())
          ) {
            const matchValue = this.quillMentionUsers[i]
            this.matches.push(matchValue)
          }
        }
        renderList(this.matches, searchTerm)
      }
    },

    emitSelectedAtMentionIDs() {
      const delta = this.editor.getContents()
      const atMentionIDs = new Set()
      if (delta.ops.length) {
        delta.ops.forEach((element: any) => {
          if (element.insert.hasOwnProperty('mention')) {
            atMentionIDs.add(parseInt(element.insert.mention.id, 0))
          }
        })
      }

      if (this.quillMentionUsers.length > 0) {
        this.$emit('mentions-list', Array.from(atMentionIDs))
      }

      const mention: HTMLElement = document.querySelector(
        '.ql-mention-list-container',
      )
      const bodyWidth: Element = document.querySelector('.el-card__body')
      const card: Element =
        document.querySelector('.child-detail-card__actions') ||
        document.querySelector('.entity-child-form') ||
        document.querySelector('.dd-modal__body')

      // quill-mention only adds its list to the page while it's open
      if (card && mention && this.quillMentionUsers.length > 0) {
        const mentionLeft = parseFloat(window.getComputedStyle(mention).left)
        const width = parseFloat(window.getComputedStyle(mention).width)
        const cardWidth =
          parseFloat(window.getComputedStyle(bodyWidth).width) -
          parseFloat(
            window.getComputedStyle(card).getPropertyValue('padding-left'),
          )

        const total = mentionLeft + width

        if (total > cardWidth) {
          const remainingRight = window.innerWidth
          mention.style.transform =
            'translateX(-' + (mentionLeft + width - cardWidth + 15) + 'px)'
        } else {
          mention.style.transform = 'translateX(0)'
        }
      }
    },

    /**
     * Triggers click on button for el-upload
     */
    triggerUpload(e: any) {
      e.preventDefault()
      // @ts-ignore
      this.$refs.btnUpload.click()
    },

    handlerPicker(e: any) {
      e.preventDefault()
    },
    async handleRemove(file: any, fileList: any) {
      try {
        this.fileList = this.fileList.filter((item: any) => item.id !== file.id)
        this.filelistRemoved.push(file.id)
        this.hasAttachment = this.fileList.length > 0
        this.$emit('file-list-removed', file.id)
      } catch (error) {
        this.$messaging.showGenericError()
      }
    },

    handleError(err: any, file: any, fileList: any) {
      this.fileList = fileList
    },

    handleChange(file: any, fileList: any) {
      // The API turns a file this large away, but closes the connection while the browser is still sending it, so the
      // browser never reads that answer and the upload fails with only a generic error
      if (FileUploadAssistant.isTooLarge(file.size)) {
        this.$messaging.showError(FileUploadAssistant.tooLargeMessage(file.name))

        // el-upload passes its own list, which already shows the file (dropped files come with this.fileList instead).
        // Matched by uid: when several files are chosen at once, the list holds the earlier ones as reactive proxies.
        const index = fileList.findIndex((item: any) => item.uid === file.uid)
        if (index > -1) {
          fileList.splice(index, 1)
        }
        return
      }

      try {
        const reader = new FileReader()
        this.hasAttachment = true
        reader.onload = async () => {
          try {
            this.isMobileActionSheetOpen = false
            this.uploadingFile = true
            let response

            if (this.ticketPortalConvoId) {
              response = await TicketPortalAPI.post_FilesUpload(
                this.ticketPortalConvoId,
                {
                  files: [{ file: reader.result.toString() }],
                  form: [{ name: file.name }],
                },
              )
            } else {
              response = await FilesAPI.post_Upload({
                files: [{ file: reader.result.toString() }],
                form: [{ name: file.name }],
              })
            }
            if (response.id) {
              file.id = response.id
              file.uid = file.uid ? file.uid : Date.now() + file.id
              this.fileList.push(file)

              const upload = this.fileList.filter(
                item => item.status === 'ready',
              )

              this.$emit('files-list', upload)
            } else {
              this.hasAttachment = this.fileList.length > 0
              throw new Error()
            }
          } catch (error) {
            this.$error.emit(error, true)
          } finally {
            this.uploadingFile = false
          }
        }

        reader.readAsDataURL(file.raw)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    overwriteBody(value: string) {
      this.setHTML(value)
    },

    handleFocus() {
      if (!this.isEditorFocused) {
        this.isEditorFocused = true
      }
    },

    handleBlur() {
      if (this.isEditorFocused) {
        this.isEditorFocused = false
      }
    },

    /**
     * Handle possibility of multiple files to be uploaded
     */
    handleGoogleDriveCallback(docs: any) {
      docs.forEach((element: any) => {
        this.handleGoogleDriveUpload(element)
      })
    },

    /**
     * Upload each file to DD api
     */
    async handleGoogleDriveUpload(file: any) {
      try {
        // Prevent file names bigger than 256 characters (API doesn't support bigger strings)
        if (file.name && file.name.length > 255) {
          this.$messaging.showError(
            'File name must be smaller than 255 characters.',
          )
          return
        }

        this.uploadingFile = true
        const response = await FilesAPI.post_LinkFromGoogleDrive({
          files: [{ fileUrl: file.url }],
          form: [{ name: file.name }],
        })
        if (response.id) {
          const newFile = {
            id: response.id,
            name: file.name,
            percentage: 0,
            size: file.size,
            status: 'ready',
            uid: file.uid,
          }

          this.fileList.push(newFile)
          const upload = this.fileList.filter(item => item.status === 'ready')

          this.$emit('files-list', upload)
          this.hasAttachment = true
        } else {
          this.hasAttachment = this.fileList.length > 0
          throw new Error()
        }
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.uploadingFile = false
      }
    },
  },
})
</script>

<style lang="scss">
/* Variables */
$border-color--default: $color--gray-30;
$border-color--focus: $color--blue;
$hover-darken-percentage: 3%;
$editor-height: 217px;

.editor {
  position: relative;

  /* Fixed height editor option */
  &--fixed-height {
    .ql-editor {
      height: $editor-height;
    }
  }

  /* Disabled */
  &--disabled {
    opacity: 0.7;
  }

  /* Special override for disabling default toolbar stickyness */
  &--disable-toolbar-stickyness .ql-toolbar {
    @include media-breakpoint-up(lg) {
      position: static !important;
    }
  }

  /* Editor canvas (editor contents, drag-and-drop area, email signature) */
  &__canvas {
    display: flex;
    flex-wrap: wrap;
    background-color: var(--color-background-header);
    border: 1px solid $border-color--default;
    border-top: none;
    border-radius: 0 0 8px 8px;
    position: inherit;
    z-index: 2;
    min-height: $editor-height;
    height: 100%;
    width: 100%;

    &.flat-bottom {
      border-radius: 0;
    }

    .ql-hidden {
      display: none;
    }

    /* Editor contents (RTE, clipboard, link tooltip, @-mention list)) */
    &--content.ql-container.ql-snow {
      width: 100%;
      height: auto;
      border: none !important; // override quilljs border

      /* RTE WYSIWYG CSS should match the content on save */
      > .ql-editor {
        // Placeholder font...
        font-size: 16px;
        width: 100%;
        min-height: 100%;
        padding: 13px 15px;

        &.ql-blank::before {
          font-style: normal;
          color: $color--gray-60 !important;
          text-transform: none;
          opacity: 0.5;
        }
      }

      /* Link tooltip */
      > .ql-tooltip {
        border-radius: 8px;
        box-shadow: $--select-dropdown-shadow;
        height: 46px;
        line-height: 34px;

        /* Link label */
        &[data-mode='link']::before,
        &.ql-tooltip::before {
          color: var(--color-text-secondary);
          font-weight: $bold;
        }

        /* Link text field */
        input {
          padding: 0 8px;
          height: 30px;
          line-height: 30px;
          color: var(--color-text-secondary);
          font-size: 13px !important;
          border-radius: 8px;

          &:focus {
            border: 2px solid $color--blue !important;
            outline: none;
          }
        }

        /* Link preview */
        > .ql-preview {
          margin-top: 4px;
          @include lettering--normal-link;
        }

        /* Side buttons */
        a.ql-action,
        a.ql-remove {
          color: $color--blue;
        }
      }

      /* @-mention list */
      .ql-mention {
        &-list-container {
          background-color: var(--color-background-header);
          z-index: 2;
          min-width: 284px;
        }

        &-list {
          max-height: 146px;
          height: 100%;
          padding: 8px 6px;
          overflow-y: scroll;

          &-item {
            @include single-line-ellipsis;
            height: 32px;
            margin-bottom: 2px;
            line-height: 32px;
            border-radius: 4px;
            color: var(--color-text-third);

            &.selected {
              background-color: rgba($color--gray-60, 0.12);
            }
          }

          .mention {
            &--dark {
              font-size: 14px;
              color: var(--color-text-secondary);
            }

            &--grey {
              font-size: 12px;
              color: var(--color-text-third);
            }
          }
        }
      }
    }

    /* Drag-and-drop area */
    .dropzone {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      height: 100%;
      width: 100%;
      background-color: rgba(255, 255, 255, 0.9);
      outline: 1px dashed $color--gray-60;
      outline-offset: -10px;
      color: var(--color-text-third);
      pointer-events: none;
      transition: outline-offset 0.15s ease-in-out;

      &__label {
        display: flex;
        flex-direction: column;
        color: var(--color-text-third);

        > div {
          display: flex;
          justify-content: center;

          > .ddi {
            font-size: 24px;
            margin: 4px;

            &.ddi-file {
              transform: rotate(-8deg);
            }

            &.ddi-image {
              transform: rotate(8deg);
            }
          }
        }

        > span {
          @include lettering--p;
          color: var(--color-text-third);
        }
      }

      &__drag-over {
        outline-offset: -16px;
      }
    }

    /* Email signature */
    .content-signature {
      padding: 0px 15px 13px 15px;
      max-width: 702px;

      a {
        /* Overrides _reboot.scss */
        text-decoration: underline;
      }

      .signature__text {
        max-width: 672px;
        color: var(--color-text-third);
        > p {
          color: var(--color-text-third);
        }
      }
    }

    /* Focus into RTE */
    &.focus {
      border: solid 2px $border-color--focus;
      min-height: $editor-height;

      /* Since we have a 2px border on the containing canvas, update padding
         of editor and signature so there is no lateral shift between focus
         and non-focused text.*/

      /* The editor */
      .ql-container.ql-snow > .ql-editor {
        padding: 11px 14px;
      }

      /* Email signature */
      .content-signature {
        padding: 0px 14px 10px 14px;
      }
    }

    /* If uploads exist, no border radius (on bottom) to meet with the upload list UI */
    &.upload {
      border-radius: 0;
    }
  }

  /* Toolbar container */
  .ql-toolbar {
    display: flex;
    background-color: var(--color-background-header);
    line-height: 1;
    border-color: $border-color--default;
    border-radius: 8px 8px 0 0;

    // Affix underneath the global header, if necessary
    @include media-breakpoint-up(lg) {
      height: 40px;
      position: sticky;
      top: calc(#{$navbar-desktop-height} - 5px);
      z-index: 3;
    }

    /* Hide certain tools on mobile.
      Doing this approach instead of v-if="isMobile" approach because
      strange things happen when responsively expanding/contracting browser. */
    @include media-breakpoint-down(sm) {
      button.ql-underline,
      button.ql-header,
      button.ql-code-block,
      button.ql-table-better {
        display: none;
      }
    }

    /* The table icon, an SVG (the icon font has none) */
    svg.toolbar-icon {
      float: none;
      width: 16px;
      height: 16px;
    }

    /* Toolbar icons */
    .toolbar-icon {
      top: -1px;
      color: var(--color-text-primary) !important;
      vertical-align: middle;
      font-size: 16px;

      &:hover {
        color: $color--blue !important;
      }

      &.ddi-bold,
      &.ddi-underline,
      &.ddi-italic {
        top: 0;
        font-size: 14px;
      }
    }

    /* Active state on toolbar icon */
    .ql-active {
      background-color: transparent !important;

      .toolbar-icon {
        color: $color--blue !important;
      }
    }
  }

  /* Insert variable dropdown */
  .insert-variable-dropdown {
    position: absolute;
    right: 0;
    z-index: 4;

    .insert-variable-button {
      display: flex;
      height: 40px;
      align-items: center;
      justify-content: center;
      padding: 6px 12px;
      border-radius: 0 8px 0 0;

      i {
        color: var(--color-text-third);
      }
    }
  }

  /* File upload UI */
  &__upload-list {
    position: relative;
    width: 100%;
    z-index: 1;
    min-height: 40px;
    padding: 8px 0;
    background-color: var(--color-background-primary);
    border: 1px solid $color--gray-30;
    border-top: none;
    border-radius: 0 0 8px 8px;

    &.flat-bottom {
      border-radius: 0;
    }

    &.uploading {
      .el-upload-list {
        @include loadingIcon(true);
      }
    }

    /* Hide default upload text/tip */
    .el-upload.el-upload--text,
    .el-upload__tip {
      display: none;
    }

    /* File upload names list */
    .el-upload-list {
      &__item {
        display: flex;
        align-items: center;
        font-size: 16px;
        color: var(--color-text-third);
        outline: none;
        transition: none;

        &-name {
          padding-left: 16px;
          margin-right: 8px;
          font-size: 13px;
          color: var(--color-text-primary) !important;
          cursor: default !important;
        }

        &-status-label {
          display: none !important;
        }

        &:hover {
          background-color: inherit;
        }

        &:first-child {
          margin-top: 0;
        }

        .el-icon-close {
          font-size: 10px;
          padding-left: 4px;
          position: relative;
          top: 0;
          font-family: 'icons' !important;
          color: $color--red;
          opacity: 1;

          /* ddi-close */
          &::before {
            content: '\f10c';
          }
        }

        &.is-success {
          color: var(--color-text-secondary);
        }

        .el-icon-close {
          display: inline-block;
        }

        .el-icon-document {
          display: none;
        }

        .el-icon-close-tip {
          display: none !important;
        }
      }
    }
  }
}
</style>
